import React, { useEffect, useState } from "react";
import { Button, Select, Tag, Tooltip, message, notification } from "antd";
import { useRecoilState } from "recoil";
import { ColumnsType } from "antd/es/table";
import { breadcrumbState } from "../../../lib/stores/breadcrumb";
import getApiEndpoints from "../../../services/endpoints";
import {
  del,
  deleteMethod,
  get,
  getQR,
  post,
} from "../../../services/apiService";
import CommonTable from "../../../lib/components/Table/table";
import {
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import DiscardChangeModal from "../../../lib/components/modals/discard-change-modal";
import { useNavigate } from "react-router-dom";
import { companyState } from "../../../lib/stores/companyId";
import CopyClipBoard from "../../../lib/components/copyClipBoard/page";
import MapIcon from "../../../lib/components/MapLocator/page";

interface DataType {
  title: string;
  width: number;
  dataIndex: string;
  key: string;
  sorter: boolean;
}

// Function to check if a string is an ISO 8601 date
const isISO8601Date = (value: any) => {
  const date = new Date(value);
  return !isNaN(date.getTime());
};

const renderStatus = (value?: any, key?: any, record?: any) => {
  if (key === "userName") {
    if (record?.fcmDataID?.userName) {
      return (
        <Tooltip title={record.fcmDataID.userName}>
          <div>{record.fcmDataID.userName}</div>
        </Tooltip>
      );
    }
  }

  if (key === "locationId") {
    if (value !== null) {
      return <CopyClipBoard text={value} />;
    } else {
      return "NA";
    }
  }
  if (key === "isActive") {
    const color = record.isActive === 1 ? "green" : "volcano";
    return (
      <div>
        <Tag color={color}>{record.isActive === 1 ? "Active" : "Inactive"}</Tag>
      </div>
    );
  }
  if (key === "locationAddress") {
    if (value !== null) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Tooltip title={value}>
            {/* <div>{value}</div> */}
            <div className="ellipsis-cell">{value}</div>
          </Tooltip>
          <MapIcon latitude={record?.latitude} longitude={record?.longitude} />
        </div>
      );
    } else {
      return "NA";
    }
  }
  if (key === "updatedAt") {
    if (value !== null && isISO8601Date(value)) {
      const date = new Date(value);
      return date.toLocaleDateString();
    }
  }

  // Handling other truthy values with ellipsis and tooltip
  if (value) {
    return (
      <Tooltip title={value}>
        <div>{value}</div>
      </Tooltip>
    );
  }

  // Default case for falsy values other than boolean false
  return "NA";
};

const OutletGroup: React.FC = () => {
  const navigate = useNavigate();
  const [, setBreadCrumb] = useRecoilState(breadcrumbState);
  const [companyId] = useRecoilState(companyState);
  const [rowData, setRowData] = useState<any>();
  const user: any =
    localStorage.getItem("user") || sessionStorage.getItem("user");
  const userData: any = JSON.parse(user);
  const userRole = userData?.roles[0];
  const apiEndpoints = getApiEndpoints(userRole);
  const initialValues = { Machine: "all", Location: "all" };
  const [menuList, setMenuList] = useState([]);
  const [selectMenu, setSelectMenu] = useState(null);

  useEffect(() => {
    setBreadCrumb([
      { label: "Masters", href: "/outlet-group" },
      { label: "eCommerce Group", href: "/outlet-group" },
    ]);
  }, [setBreadCrumb]);

  const handleEdit = (record: any) => {
    navigate(`/outlet-group/${record.outletId}`);
  };

  const handleQRCode = async (record: any) => {
    try {
      // Pass the responseType configuration to the get function
      const response = await getQR(
        `${apiEndpoints.outletgroupQRCode}/${record?.outletId}`,
        {
          responseType: "blob", // Set the response type for binary data (PNG)
        }
      );

      // Create a URL for the PNG file
      const fileURL = URL.createObjectURL(response);

      // Create an anchor element to trigger the download
      const a = document.createElement("a");
      a.href = fileURL;
      a.download = "QRCode.png"; // Specify the file name
      a.click();

      // Optional: Revoke the URL after download
      URL.revokeObjectURL(fileURL);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleDeleteConfirmation = async (itemId: string) => {
    try {
      if (itemId) {
        const response = await deleteMethod(
          `${apiEndpoints.outletgroup}/${itemId}`
        );
        if (response.status === 200) {
          notification.success({
            message: "Successfully Deleted",
          });
          await fetchOutlets();
        }
      }
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleDelete = (record: any) => {
    DiscardChangeModal(
      "Click OK to Confirm to Delete.",
      async () => {
        handleDeleteConfirmation(record?.outletId);
      },
      () => {},
      { title: "Are you sure you want to Delete ?" }
    );
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "outletName",
      key: "outletName",
      sorter: (a: any, b: any) =>
        (a?.outletName?.toString() ?? "")
          .toLowerCase()
          .localeCompare((b?.outletName?.toString() ?? "").toLowerCase()),

      render: renderStatus,
    },
    {
      title: "Desc",
      dataIndex: "outletDesc",
      key: "outletDesc",
      sorter: (a: any, b: any) =>
        (a?.outletDesc?.toString() ?? "")
          .toLowerCase()
          .localeCompare((b?.outletDesc?.toString() ?? "").toLowerCase()),

      render: renderStatus,
    },
    {
      title: "Timings",
      dataIndex: "outletTimings",
      key: "outletTimings",
      sorter: (a: any, b: any) =>
        (a?.outletTimings?.toString() ?? "")
          .toLowerCase()
          .localeCompare((b?.outletTimings?.toString() ?? "").toLowerCase()),

      render: renderStatus,
    },
    {
      title: "City",
      dataIndex: "outletCity",
      key: "outletCity",
      sorter: (a: any, b: any) =>
        (a?.outletCity?.toString() ?? "")
          .toLowerCase()
          .localeCompare((b?.outletCity?.toString() ?? "").toLowerCase()),

      render: renderStatus,
    },
    {
      title: "Photo",
      dataIndex: "outletPhoto",
      key: "outletPhoto",
      sorter: (a: any, b: any) =>
        (a?.outletPhoto?.toString() ?? "")
          .toLowerCase()
          .localeCompare((b?.outletPhoto?.toString() ?? "").toLowerCase()),

      render: renderStatus,
    },
    {
      title: "QR Code",
      dataIndex: "outletName",
      key: "outletName",
      render: (value, record) => (
        <Button type="primary" onClick={() => handleQRCode(record)}>
          QR Code
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center",
      render: (value, record) => renderStatus(value, "isActive", record),
    },

    {
      title: "Action",
      key: "operation",
      fixed: "right",
      // width: 30,
      render: (record) => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Tooltip title="Edit">
            <EditOutlined onClick={() => handleEdit(record)} />
          </Tooltip>{" "}
          &nbsp; &nbsp;&nbsp;
          <Tooltip title="Delete">
            <DeleteOutlined onClick={() => handleDelete(record)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  const fetchOutlets = async () => {
    try {
      const response = await get(apiEndpoints.outletgroup);
      if (response.status === 200) {
        setRowData(response?.data);
      }
    } catch (error) {
      console.log(error);
      message.error("Error while fetching");
    }
  };

  useEffect(() => {
    fetchOutlets();
  }, []);

  return (
    <>
      <CommonTable
        routePath="/outlet-group/add"
        showDates={false}
        showDropDowns={false}
        pageName="Mobile-Devices"
        dataSource={rowData}
        columns={columns}
        initialValues={initialValues}
        onFinish={fetchOutlets}
      />
    </>
  );
};

export default OutletGroup;
